import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import useStickyState from '../../hooks/useStickyState';
import { useQueryClient } from 'react-query';
import { translate as t } from 'hooks/useTranslations';
import usePermissions from 'data_sources/usePermissions';
import useProfile from 'data_sources/useProfile';
import { ProfileResponse } from 'types/profile_response';
import AuthUtil from 'utils/AuthUtil';
import { getOrganization } from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import { getOrgRiskPoolStatus } from 'views/App/Portal/LexID/ManageSolutions/_data_sources/useRiskPool';
import useEnabledVersionsInfo from 'views/App/Portal/AWB/Accreditation/_hooks/useEnabledVersionsInfo';
import FeaturesManager from 'utils/features/FeaturesManager';
import { NavItemDef, BASE_NAV_ITEMS } from './Portal/navItems';
import { NavItem } from '_lex/_atoms/Sidebar';
import { registerAllNavItems } from './Portal/navigationConfig';

type AppProviderProps = {
  userOrgId: string | undefined;
  children: React.ReactNode;
  isTokenValid?: boolean;
};

export const REDIR_BLACKLIST = [
  '/auth-callback.html',
  '/admin/auth-callback.html',
  '/login',
  '/admin/login',
  '/admin/dashboard',
  '/dashboard',
];

export type OrganizationPermissions = { [key: string]: string[] };
export type OrganizationRiskPoolStatus = { [orgId: string]: boolean };

export type AppProviderResponse = {
  currentModule?: string;
  currentProfile: ProfileResponse | null;
  currentOrganizationId: string;
  leftNavCollapsed: boolean;
  appPermissions: string[] | null;
  orgPermissions: OrganizationPermissions | null;
  hasAppPermission: (key: string) => boolean;
  hasNoPermission: () => boolean;
  hasPermission: (key: string, orgId: string) => boolean;
  isRiskPoolEnabled: (orgId: string) => Promise<boolean>;
  invalidateRiskPoolCache: (orgId?: string) => void;
  enforcePermissions: (keys: string[], viewComponent: any) => any;
  enforceAppPermissions: (keys: string[], viewComponent: any) => any;
  isMobile: boolean;
  isRoot: boolean;
  showNav: boolean;
  setCurrentModule: (newState: string) => void;
  setMainNavCollapsed: (newState: boolean) => void;
  setShowNav: (newState: boolean) => void;
  updatePermissions: (orgId: string, orgPermissions: string[]) => void;
  urlSegments: () => string[];
  setUserOrganizationId: (newState: string) => void;
  userOrganizationId: string | undefined;
  browserTitle: string;
  setBrowserTitle: (newState: string) => void;
  setPollForPermissionChanges: (newState: boolean) => void;
  features: Record<string, boolean>;
  isFeatureEnabled: (featureId: string) => boolean;
  setFeatureEnabled: (featureId: string, enabled: boolean) => void;
  hasPermissionError: boolean;
  navItems: NavItemDef[];
  registerNavItems: (parentKey: string, items: NavItemDef[]) => void;
  sidebarItems: NavItem[];
  isLexipolStaff: boolean;
  isTokenValid?: boolean;
};

export const AppContext = React.createContext<AppProviderResponse>({} as AppProviderResponse);

// Helper function to check if a URL should be stored in localStorage
export const shouldStoreUrl = (pathname: string): boolean => {
  // Check if the pathname is in the blacklist
  if (REDIR_BLACKLIST.includes(pathname)) {
    // console.log('Not storing URL - in blacklist:', pathname);
    return false;
  }

  // Check if the pathname contains auth-callback
  if (pathname.includes('auth-callback')) {
    // console.log('Not storing URL - contains auth-callback:', pathname);
    return false;
  }

  return true;
};

// Helper function to safely store URL with logging
export const storeLastUrl = (location: any): void => {
  if (shouldStoreUrl(location.pathname)) {
    // console.log('Setting lastUrl:', location.pathname);
    localStorage.setItem('phx-lastUrl', JSON.stringify(location));
  } else {
    // console.log('Skipping lastUrl storage for:', location.pathname);
  }
};

const AppProvider: ({ userOrgId, children }: AppProviderProps) => any = ({ userOrgId, children, isTokenValid }) => {
  const currLoc = useLocation();
  const urlSegments = useCallback(() => {
    return currLoc.pathname.slice(1).split('/');
  }, [currLoc.pathname]);
  const navigate = useNavigate();
  const theme = useTheme();
  const token = AuthUtil.getToken();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentModule, setCurrentModule] = useState<string>();
  const [showNav, setShowNav] = useState<boolean>(false);
  const [leftNavCollapsed, setMainNavCollapsed] = useStickyState<boolean>('left-nav-collapsed', false);
  const [userOrganizationId, setUserOrganizationId] = useState<string | undefined>(() => {
    const stored = localStorage.getItem('phx-userOrgId');
    return stored || userOrgId;
  });
  const [isRoot, setIsRoot] = useStickyState<boolean>('phx-isRoot', false);
  const [browserTitle, setBrowserTitle] = useState<string>(t('common.browserTitle'));
  const [pollForPermissionChanges, setPollForPermissionChanges] = useState<boolean>(false);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  const [hasPermissionError, setHasPermissionError] = useState<boolean>(false);

  const [currentProfile, setCurrentProfile] = useStickyState<ProfileResponse | null>('phx-currentProfile', null);
  const [orgPermissions, setOrgPermissions] = useStickyState<OrganizationPermissions | null>(
    'phx-orgPermissions',
    null,
  );
  const [appPermissions, setAppPermissions] = useStickyState<string[] | null>('phx-appPermissions', null);
  const [features, setFeatures] = useStickyState<Record<string, boolean>>('portal_features', {
    'policy-headers': false,
    awb: false,
  });
  const [navItems, setNavItems] = useState<NavItemDef[]>(BASE_NAV_ITEMS);
  const [orgRiskPoolStatus, setOrgRiskPoolStatus] = useStickyState<OrganizationRiskPoolStatus | null>(
    'phx-orgRiskPoolStatus',
    null,
  );

  useEffect(() => {
    if (currLoc) {
      storeLastUrl(currLoc);
    }
  }, [currLoc]);

  useEffect(() => {
    document.title = browserTitle;
  }, [browserTitle]);

  const updatePermissions = useCallback(
    (orgId: string, orgPermissions: string[]) => {
      setOrgPermissions((prev: OrganizationPermissions | null) => ({
        ...prev,
        [orgId]: orgPermissions.sort(),
      }));
    },
    [setOrgPermissions],
  );

  const queryClient = useQueryClient();

  const {
    data: appPermissionsData,
    isLoading: isAppPermissionsLoading,
    isError: isAppPermissionsError,
    error: appPermissionsError,
  } = usePermissions(
    {
      enabled: !AuthUtil.isRoot() && !AuthUtil.isTokenExpired(),
      // staleTime: 30 * 60 * 1000,
      // cacheTime: 30 * 60 * 1000,
      refetchInterval: 1000 * 60 * 5,
    },
    (errors) => {
      console.error('Error fetching application permissions:', errors);
      setHasPermissionError(true);
    },
  );
  const {
    data: profileData,
    isLoading: isProfileLoading,
    isError: isProfileError,
    error: profileError,
  } = useProfile(
    {
      enabled: !AuthUtil.isRoot() && !AuthUtil.isTokenExpired(),
      refetchInterval: 1000 * 60 * 5,
      // staleTime: 30 * 60 * 1000,
      // cacheTime: 30 * 60 * 1000,
    },
    (errors) => {
      console.error('Error fetching profile:', errors);
      setHasPermissionError(true);
    },
  );

  useEffect(() => {
    if (appPermissionsData && !isAppPermissionsLoading) {
      setAppPermissions(appPermissionsData.map((x) => x.action).sort());
    }
    if (isAppPermissionsError) {
      console.error('Error fetching application permissions:', appPermissionsError);
      setHasPermissionError(true);
    }
  }, [appPermissionsData, isAppPermissionsLoading, isAppPermissionsError, appPermissionsError, setAppPermissions]);

  useEffect(() => {
    if (profileData && !isProfileLoading) {
      setCurrentProfile(profileData);
      if (profileData?.authorizations && profileData?.organization) {
        updatePermissions(
          profileData.organization.id,
          (profileData.authorizations || []).map((x) => x.action),
        );
      }
    }
    if (isProfileError) {
      console.error('Error fetching profile:', profileError);
      setHasPermissionError(true);
    }
  }, [profileData, isProfileLoading, isProfileError, profileError, setCurrentProfile, updatePermissions]);

  useEffect(() => {
    if (token) {
      if (AuthUtil.isRoot()) {
        setIsRoot(true);
        const rootProfile: ProfileResponse = {
          user: {
            username: 'root',
            firstName: 'Root',
            lastName: 'User',
            email: '',
            orgId: 'LexipolGenesisRoot',
          },
          organization: {
            id: 'LexipolGenesisRoot',
            name: 'Lexipol',
            city: '',
            state: '',
            country: '',
          },
        };
        if (AuthUtil.shouldAllowStorageOperations()) {
          localStorage.setItem('phx-currentProfile', JSON.stringify(rootProfile));
        }
        setCurrentProfile(rootProfile);
      } else {
        setIsRoot(false);
        queryClient.invalidateQueries(['permissions', token]);
        queryClient.invalidateQueries(['profile', token]);
      }
    }
  }, [token, queryClient, setIsRoot, setCurrentProfile]);

  const hasPermission: (key: string, orgId: string) => boolean = useCallback(
    (key, orgId) => {
      // First check regular org permissions
      if (orgPermissions && orgPermissions[orgId]) {
        if (orgPermissions[orgId].includes(key)) {
          return true;
        }
      } else {
        const orgPerms: OrganizationPermissions = JSON.parse(localStorage.getItem('phx-orgPermissions') || '{}');
        if (orgPerms && orgPerms[orgId]) {
          if (orgPerms[orgId].includes(key)) {
            return true;
          }
        }
      }

      // Then check feature-based org permissions
      const allFeatures = FeaturesManager.getInstance().getAllFeatures();
      for (const feature of allFeatures) {
        if (feature.enabled && feature.orgPermissions && feature.orgPermissions.includes(key)) {
          return true;
        }
      }

      return false;
    },
    [orgPermissions],
  );

  const hasAppPermission: (key: string) => boolean = useCallback(
    (key) => {
      // First check regular app permissions
      if (appPermissions && appPermissions.includes(key)) {
        return true;
      }

      // Then check feature-based app permissions
      const allFeatures = FeaturesManager.getInstance().getAllFeatures();
      for (const feature of allFeatures) {
        if (feature.enabled && feature.appPermissions && feature.appPermissions.includes(key)) {
          return true;
        }
      }

      return false;
    },
    [appPermissions],
  );

  const hasNoPermission: () => boolean = useCallback(() => {
    let result = false;
    const currentOrgId = localStorage.getItem('phx-currentOrganizationId')?.toString() || userOrganizationId;
    const orgPermissionsCount =
      orgPermissions && currentOrgId && orgPermissions[currentOrgId] ? orgPermissions[currentOrgId].length : 0;
    const appPermissionsCount = appPermissions ? appPermissions.length : 0;
    if (currentProfile && orgPermissionsCount + appPermissionsCount === 0) {
      result = true;
    }
    return result;
  }, [appPermissions, currentProfile, orgPermissions, userOrganizationId]);

  // Check if Risk Pool is enabled for a specific organization
  const isRiskPoolEnabled: (orgId: string) => Promise<boolean> = useCallback(
    async (orgId) => {
      // Check cache first
      if (orgRiskPoolStatus && orgId in orgRiskPoolStatus) {
        return orgRiskPoolStatus[orgId];
      }

      // Check if user has permission to access risk pool endpoint
      // This endpoint requires product management permissions (listOrgSolutions or similar)
      // If user doesn't have permission, return false without making the API call
      const hasManageSolutionsPermission = hasPermission('listOrgSolutions', orgId);
      if (!hasManageSolutionsPermission) {
        // Cache false to avoid repeated checks
        setOrgRiskPoolStatus((prev: OrganizationRiskPoolStatus | null) => ({
          ...prev,
          [orgId]: false,
        }));
        return false;
      }

      // Fetch from API if not cached
      try {
        const isEnabled = await getOrgRiskPoolStatus(orgId);

        // Update cache
        setOrgRiskPoolStatus((prev: OrganizationRiskPoolStatus | null) => ({
          ...prev,
          [orgId]: isEnabled,
        }));

        return isEnabled;
      } catch (error) {
        console.error('Error checking risk pool status:', error);
        return false;
      }
    },
    [orgRiskPoolStatus, setOrgRiskPoolStatus, hasPermission],
  );

  // Invalidate risk pool cache for a specific org or all orgs
  const invalidateRiskPoolCache = useCallback(
    (orgId?: string) => {
      if (orgId) {
        // Remove specific org from cache, forcing a refetch next time
        setOrgRiskPoolStatus((prev: OrganizationRiskPoolStatus | null) => {
          if (!prev) return prev;
          const { [orgId]: _, ...rest } = prev;
          return rest;
        });
      } else {
        // Clear entire cache
        setOrgRiskPoolStatus(null);
      }
    },
    [setOrgRiskPoolStatus],
  );

  useEffect(() => {
    if (isTokenValid && userOrganizationId && AuthUtil.shouldAllowStorageOperations()) {
      localStorage.setItem('phx-userOrgId', userOrganizationId);
    }
  }, [userOrganizationId, isTokenValid]);

  useEffect(() => {
    if (
      isTokenValid &&
      AuthUtil.shouldAllowStorageOperations() &&
      (localStorage.getItem('phx-currentOrganizationId') === null ||
        localStorage.getItem('phx-currentOrganizationId') === '')
    ) {
      localStorage.setItem('phx-currentOrganizationId', userOrganizationId || '');
    }
  }, [userOrganizationId, isTokenValid]);

  const currentOrganizationId = localStorage.getItem('phx-currentOrganizationId')?.toString() || userOrganizationId;

  const isLexipolStaff = currentOrganizationId === 'lexipoladminstaffuuid';
  const shouldFetchAccreditationVersions =
    !!currentOrganizationId &&
    !isLexipolStaff &&
    hasPermission('listAccreditingBodies', currentOrganizationId || '');
  const versionsInfo = useEnabledVersionsInfo(shouldFetchAccreditationVersions ? currentOrganizationId || '' : '');
  const hasEnabledAccreditationVersion = shouldFetchAccreditationVersions
    ? !versionsInfo.hasNoEnabledVersions
    : undefined;

  // Preload risk pool status for current organization
  useEffect(() => {
    if (currentOrganizationId && currentOrganizationId !== 'LexipolGenesisRoot') {
      // Check if already cached
      if (!orgRiskPoolStatus || !(currentOrganizationId in orgRiskPoolStatus)) {
        // Fetch and cache the risk pool status
        isRiskPoolEnabled(currentOrganizationId).catch((error) => {
          console.error('Error preloading risk pool status:', error);
        });
      }
    }
  }, [currentOrganizationId, orgRiskPoolStatus, isRiskPoolEnabled]);

  const enforcePermissions: (keys: string[], viewComponent: any) => any = useCallback(
    (keys, viewComponent) => {
      if (keys.every((k) => hasPermission(k, currentOrganizationId || ''))) {
        return viewComponent;
      }
      return <Navigate to='/admin/dashboard' />;
    },
    [currentOrganizationId, hasPermission],
  );

  const enforceAppPermissions: (keys: string[], viewComponent: any) => any = useCallback(
    (keys, viewComponent) => {
      if (keys.every((k) => hasAppPermission(k))) {
        return viewComponent;
      }
      return <Navigate to='/admin/dashboard' />;
    },
    [hasAppPermission],
  );

  useEffect(() => {
    if (pollForPermissionChanges && currentOrganizationId) {
      let pollCount = 0;
      const maxPolls = 10; // 10 polls * 3 seconds = 30 seconds total

      const poll = () => {
        if (pollCount >= maxPolls) {
          setPollForPermissionChanges(false);
          return;
        }

        getOrganization(currentOrganizationId)
          .then((org) => {
            if (org?.organization?.authorizations) {
              const newPermissions = org.organization.authorizations.map((x: { action: string }) => x.action).sort();
              const currentPermissions = orgPermissions?.[currentOrganizationId]?.sort() || [];

              if (JSON.stringify(newPermissions) !== JSON.stringify(currentPermissions)) {
                updatePermissions(currentOrganizationId, newPermissions);
              }
            }
            pollCount++;
          })
          .catch((error) => {
            console.error('Error polling for permission changes:', error);
            pollCount++;
          });
      };

      // Initial poll
      poll();

      // Set up interval for subsequent polls
      const interval = setInterval(poll, 3000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [pollForPermissionChanges, currentOrganizationId, orgPermissions, updatePermissions]);

  // Sync features with FeaturesManager
  useEffect(() => {
    // Clean up any features that are no longer registered in code
    FeaturesManager.cleanupRemovedFeatures();

    const allFeatures = FeaturesManager.getInstance().getAllFeatures();
    const featuresMap = allFeatures.reduce(
      (acc, feature) => ({
        ...acc,
        [feature.id]: feature.enabled,
      }),
      {},
    );
    setFeatures(featuresMap);
  }, []);

  const isFeatureEnabled = useCallback(
    (featureId: string) => {
      // Use both local state and FeaturesManager to ensure we have the latest value
      return FeaturesManager.getInstance().isFeatureEnabled(featureId) || features[featureId];
    },
    [currentProfile, features],
  );

  const setFeatureEnabled = useCallback(
    (featureId: string, enabled: boolean) => {
      FeaturesManager.getInstance().setFeatureEnabled(featureId, enabled);
      // Update local state immediately for responsive UI
      setFeatures((prev) => ({
        ...prev,
        [featureId]: enabled,
      }));
    },
    [setFeatures],
  );

  const registerNavItems = useCallback((parentKey: string, items: NavItemDef[]) => {
    setNavItems((prevItems) => {
      // Find the parent item
      const parentItem = prevItems.find((item) => item.key === parentKey);
      if (!parentItem) return prevItems;

      // Create a new array with the updated children
      return prevItems.map((item) => {
        if (item.key === parentKey) {
          // Merge existing children with new items, ensuring uniqueness by key
          const existingChildren = item.children || [];
          const newChildren = [...existingChildren];

          items.forEach((newItem) => {
            const existingIndex = newChildren.findIndex((child) => child.key === newItem.key);
            if (existingIndex === -1) {
              newChildren.push(newItem);
            } else {
              const existingItem = newChildren[existingIndex];
              // Merge properties: prefer newItem but preserve visibility from existing if newItem doesn't have it
              // This ensures visibility functions aren't lost during re-registration
              const mergedItem = {
                ...existingItem,
                ...newItem,
                // Always prefer newItem's visibility if it exists, otherwise preserve existing
                visibility: newItem.visibility ?? existingItem.visibility,
              };
              newChildren[existingIndex] = mergedItem;
            }
          });

          return {
            ...item,
            children: newChildren,
          };
        }
        return item;
      });
    });
  }, []);

  const navItemAllowed = useCallback(
    (item: NavItemDef) => {
      let allowed = true;

      if (item.permissions) {
        const matchingPerms = item.permissions.filter((x) => hasPermission(x, currentOrganizationId || ''));
        if (matchingPerms.length === 0) allowed = false;
      }
      if (item.appPermissions) {
        const matchingPerms = item.appPermissions.filter((x) => hasAppPermission(x));
        if (matchingPerms.length === 0) allowed = false;
      }
      if (item.featurePermission && !isFeatureEnabled(item.featurePermission)) {
        allowed = false;
      }
      if (item.requiresRiskPool) {
        // Check cached risk pool status
        const riskPoolEnabled = orgRiskPoolStatus?.[currentOrganizationId || ''] || false;
        if (!riskPoolEnabled) {
          allowed = false;
        }
      }
      if (item.sameOrg) {
        if ((userOrganizationId ?? '') !== (currentOrganizationId ?? '')) {
          allowed = false;
        }
      }
      if (item.orgContext) {
        if ((userOrganizationId ?? '') === (currentOrganizationId ?? '')) {
          allowed = false;
        }
      }
      if (item.rootOnly) {
        if (currentOrganizationId !== 'lexipoladminstaffuuid') {
          allowed = false;
        }
      }
      if (item.nonRoot) {
        if ((currentOrganizationId ?? '') === 'LexipolGenesisRoot') {
          allowed = false;
        }
      }
      if (item.visibility) {
        const visibilityResult = item.visibility({
          hasPermission,
          hasAppPermission,
          isFeatureEnabled,
          currentOrganizationId: currentOrganizationId || '',
          hasEnabledAccreditationVersion,
        });
        if (!visibilityResult) {
          allowed = false;
        }
      }
      return allowed;
    },
    [
      currentOrganizationId,
      hasPermission,
      hasAppPermission,
      isFeatureEnabled,
      userOrganizationId,
      orgRiskPoolStatus,
      hasEnabledAccreditationVersion,
    ],
  );

  const moduleAllowed = useCallback(
    (item: NavItemDef) => {
      let allowed = true;
      if (!navItemAllowed(item)) {
        allowed = false;
      }
      // Only check feature permissions for modules
      if (item.featurePermission && !isFeatureEnabled(item.featurePermission)) {
        allowed = false;
      }
      return allowed;
    },
    [isFeatureEnabled, navItemAllowed],
  );

  const convertToNavItem = useCallback(
    (item: NavItemDef): NavItem => {
      return {
        key: item.key,
        icon: item.icon,
        label: t(item.label || ''),
        subtitle: item.subtitle ? t(item.subtitle) : undefined,
        divider: item.divider,
        href: item.path ? `/admin/${item.path}` : item.href || '',
        newWindow: item.newWindow,
        permissions: item.permissions,
        appPermissions: item.appPermissions,
        featurePermission: item.featurePermission,
        sameOrg: item.sameOrg,
        orgContext: item.orgContext,
        rootOnly: item.rootOnly,
        nonRoot: item.nonRoot,
        alwaysShow: item.alwaysShow,
        children:
          item.children?.filter(navItemAllowed)?.map((child) => ({
            key: child.key,
            label: t(child.label || ''),
            subtitle: child.subtitle ? t(child.subtitle) : undefined,
            divider: child.divider,
            href: child.path ? `/admin/${item.path}/${child.path}` : child.href || '',
            newWindow: child.newWindow,
            permissions: child.permissions,
            appPermissions: child.appPermissions,
            featurePermission: child.featurePermission,
            requiresRiskPool: child.requiresRiskPool,
            sameOrg: child.sameOrg,
            orgContext: child.orgContext,
            rootOnly: child.rootOnly,
            nonRoot: child.nonRoot,
            children:
              child.children?.filter(navItemAllowed)?.map((grandChild) => ({
                key: grandChild.key,
                label: t(grandChild.label || ''),
                subtitle: grandChild.subtitle ? t(grandChild.subtitle) : undefined,
                divider: grandChild.divider,
                href: grandChild.path ? `/admin/${item.path}/${child.path}/${grandChild.path}` : grandChild.href || '',
                newWindow: grandChild.newWindow,
                permissions: grandChild.permissions,
                appPermissions: grandChild.appPermissions,
                featurePermission: grandChild.featurePermission,
                sameOrg: grandChild.sameOrg,
                orgContext: grandChild.orgContext,
                rootOnly: grandChild.rootOnly,
                nonRoot: grandChild.nonRoot,
                children: [],
              })) || [],
          })) || [],
      };
    },
    [navItemAllowed, t],
  );

  const sidebarItems = useMemo(() => {
    const allowedItems = navItems.filter(moduleAllowed);
    const convertedItems = allowedItems.map(convertToNavItem);

    // Filter out parent items that have no link (no href and no path) and no children after filtering
    // Always show items with alwaysShow flag or dashboard (for backward compatibility)
    return convertedItems.filter((item, index) => {
      const originalItem = allowedItems[index];
      const hasHref = originalItem.href !== undefined && originalItem.href !== '';
      const hasPath = originalItem.path !== undefined && originalItem.path !== '';
      const hasLink = hasHref || hasPath;
      const hasChildren = item.children && item.children.length > 0;

      if (originalItem.alwaysShow || item.key === 'dashboard') {
        return true;
      }

      if (!hasLink && !hasChildren) {
        return false;
      }

      return true;
    });
  }, [navItems, moduleAllowed, convertToNavItem]);

  const contextValue = useMemo(() => {
    return {
      appPermissions,
      orgPermissions,
      currentModule,
      currentOrganizationId: currentOrganizationId || '',
      currentProfile,
      isMobile,
      isRoot,
      urlSegments,
      leftNavCollapsed,
      enforcePermissions,
      enforceAppPermissions,
      hasAppPermission,
      hasNoPermission,
      hasPermission,
      isRiskPoolEnabled,
      invalidateRiskPoolCache,
      setCurrentModule,
      showNav,
      setShowNav,
      setMainNavCollapsed,
      setUserOrganizationId,
      updatePermissions,
      userOrganizationId,
      browserTitle,
      setBrowserTitle,
      setPollForPermissionChanges,
      features,
      isFeatureEnabled,
      setFeatureEnabled,
      hasPermissionError,
      navItems,
      registerNavItems,
      sidebarItems,
      isLexipolStaff: currentOrganizationId === 'lexipoladminstaffuuid',
    };
  }, [
    appPermissions,
    orgPermissions,
    currentModule,
    currentOrganizationId,
    currentProfile,
    isMobile,
    isRoot,
    urlSegments,
    leftNavCollapsed,
    enforcePermissions,
    enforceAppPermissions,
    hasAppPermission,
    hasNoPermission,
    hasPermission,
    isRiskPoolEnabled,
    invalidateRiskPoolCache,
    showNav,
    setMainNavCollapsed,
    setUserOrganizationId,
    updatePermissions,
    userOrganizationId,
    browserTitle,
    setPollForPermissionChanges,
    features,
    isFeatureEnabled,
    setFeatureEnabled,
    hasPermissionError,
    navItems,
    registerNavItems,
    sidebarItems,
  ]);

  // Register all navigation items when the app initializes
  useEffect(() => {
    registerAllNavItems(registerNavItems);
  }, [registerNavItems]);

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppProvider;
